import {
  Box,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppSelector } from "src/app/hooks";
import { useCardForm } from "src/hooks/useCardForm";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";

interface SofaScoreFormData {
  SS_ID: string;
  PROCESS_TIME: Date | null;
  CTIME: Date;
  CUSER: string;
  NURSE_ID: string;
  RESPIRATORY: string;
  COAGULATION: string;
  LIVER: string;
  CARDIOVASCULAR: string;
  CENTRAL_NERVOUS_SYSTEM: string;
  RENAL_CREATININE: string;
  RENAL_URINE_OUTLET: string;
  RESULT: number;
  NURSE_NOTE: string;
}

type SofaFieldKey =
  | "RESPIRATORY"
  | "COAGULATION"
  | "LIVER"
  | "CARDIOVASCULAR"
  | "CENTRAL_NERVOUS_SYSTEM"
  | "RENAL_CREATININE"
  | "RENAL_URINE_OUTLET";

interface SofaScoreProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id: string) => Promise<void>;
  handleChange: (field: keyof SofaScoreFormData, value: any) => void;
  patientData?: any;
  isSaving?: boolean;
  readOnly?: boolean;
  extraAccess?: boolean;
}

function SofaScoreCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  onClose,
  isSaving,
  readOnly,
  extraAccess,
}: SofaScoreProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    getScoreValue,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "SOFA_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof SofaScoreFormData, value);
      });
    },
  });

  const typedFormData = formData as unknown as SofaScoreFormData;

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof SofaScoreFormData, value);
    });
  }, [formData, parentHandleChange]);

  const sofaFieldConfigs = useMemo<
    Array<{ key: SofaFieldKey; label: string; sectionTitle?: string }>
  >(
    () => [
      { key: "RESPIRATORY", label: t("sofa-score.respiratory-pao2"), sectionTitle: t("sofa-score.respiratory") },
      { key: "COAGULATION", label: t("sofa-score.platelet-count"), sectionTitle: t("sofa-score.coagulation") },
      { key: "LIVER", label: t("sofa-score.bilirubin"), sectionTitle: t("sofa-score.liver") },
      { key: "CARDIOVASCULAR", label: t("sofa-score.hypotension"), sectionTitle: t("sofa-score.cardiovascular") },
      { key: "CENTRAL_NERVOUS_SYSTEM", label: t("sofa-score.glasgow-coma-scale"), sectionTitle: t("sofa-score.central-nervous-system") },
      { key: "RENAL_CREATININE", label: t("sofa-score.creatinine"), sectionTitle: t("sofa-score.renal") },
      { key: "RENAL_URINE_OUTLET", label: t("sofa-score.urine-output") },
    ],
    [t]
  );

  const calculateScore = useCallback(() => {
    return sofaFieldConfigs.reduce((total, config) => {
      return total + getScoreValue(config.key);
    }, 0);
  }, [sofaFieldConfigs, getScoreValue]);

  useEffect(() => {
    const totalScore = calculateScore();
    if ((typedFormData.RESULT || 0) !== totalScore) {
      handleChange("RESULT", totalScore);
    }
  }, [calculateScore, handleChange, typedFormData.RESULT]);


  return (
    <>
      <ByzMuiSnackBar
        open={!!error?.open}
        onClose={() => setError(null)}
        title={t("error")}
        message={error?.message || ""}
        type="error"
      />

      <ByzMuiSnackBar
        open={successSnackbarOpen}
        onClose={() => setSuccessSnackbarOpen(false)}
        title={t("common.success")}
        message={
          isEditMode
            ? t("card.record-updated-successfully")
            : t("card.record-added-successfully")
        }
        type="success"
      />

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              loading={isSaving}
              onClick={async () => {
                try {
                  // Save öncesi tüm formData'yı parent'a aktar (hem handleChange hem de formData güncellemesi için)
                  Object.entries(formData).forEach(([key, value]) => {
                    parentHandleChange(key as keyof SofaScoreFormData, value);
                  });

                  await save(typedFormData.SS_ID || "");
                  setSuccessSnackbarOpen(true);
                  if (onClose) {
                    setTimeout(() => {
                      onClose();
                    }, 1500);
                  }
                } catch (err) {
                  console.error("Save error:", err);
                }
              }}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            {/* Ana Başlık */}
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("sofa-score.title")}
              </ByzMuiTypography>
            </Grid>

            {/* Tarih/Saat */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  minHeight: "56px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{
                  width: "100%",
                  minHeight: "56px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    }
                  }}
                  label={t("datetime")}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            {/* Oluşturan */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  minHeight: "56px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{
                  minHeight: "56px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER || ""}
                  disabled
                />
              </Grid>
            </Grid>

            {sofaFieldConfigs.map(({ key, label, sectionTitle }) => {
              const mcpConfig = cardData?.mcpConfig?.[key];
              const cachedData =
                mcpConfig && getMCPParameterDetails
                  ? getMCPParameterDetails(mcpConfig.mcpId)
                  : undefined;
              return (
                <Box key={key}>
                  {sectionTitle && (
                    <Grid
                      md={6}
                      sx={{
                        backgroundColor: "#F4F7FE",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      <ByzMuiTypography isTitle={true}>{sectionTitle}</ByzMuiTypography>
                    </Grid>
                  )}
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Grid
                      xs={12}
                      sm={6}
                      md={6}
                      sx={{
                        borderBottom: "1px solid #E0E0E0",
                        minHeight: "56px",
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                        {label}
                      </ByzMuiTypography>
                    </Grid>
                    <Grid
                      xs={12}
                      sm={6}
                      md={6}
                      sx={{
                        minHeight: "56px",
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      <ByzMcParameterDetailSelect
                        sx={{ width: "350px", marginLeft: "10px" }}
                        title={t("select")}
                        mcpId={mcpConfig?.mcpId ?? 0}
                        value={selectedMCPs[key]}
                        multiple={false}
                        handleChange={(val) => {
                          if (val && !Array.isArray(val)) {
                            handleMCPChange(
                              key,
                              val as McParameterDetailResponseDto
                            );
                          } else {
                            handleMCPChange(key, null);
                          }
                        }}
                        cachedData={cachedData}
                        cacheLoaded={mcpCacheLoaded}
                        readOnly={!extraAccess}
                      />
                    </Grid>
                  </Grid>
                </Box>
              );
            })}

            {/* Diğer Başlık */}
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>{t("other")}</ByzMuiTypography>
            </Grid>

            {/* Skor */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  minHeight: "56px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("score")}
                </ByzMuiTypography>
              </Grid>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{
                  minHeight: "56px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("score")}
                  value={typedFormData.RESULT || 0}
                  disabled
                />
              </Grid>
            </Grid>

            {/* Not */}
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  minHeight: "56px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("note")}
                </ByzMuiTypography>
              </Grid>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{
                  minHeight: "56px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("note")}
                  value={typedFormData.NURSE_NOTE || ""}
                  onChange={(e) => handleChange("NURSE_NOTE", e.target.value)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default SofaScoreCreate;
